import {
  Alert,
  Button,
  Breadcrumb,
  Form,
  Input,
  Radio,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  ClockCircleOutlined,
  EyeOutlined,
  HomeOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import moment from "moment";
import { openNotification } from "../../../components/Notification";
import PageHeader from "../../../components/PageHeader";
import { SecaoCard } from "../../../components/SecaoCard";
import { SelectBuscavel } from "../../../components/SelectBuscavel";
import { ConteudoFormatado } from "../../../components/MensagensGlobais/ConteudoFormatado";
import { EditorConteudo } from "../../../components/MensagensGlobais/EditorConteudo";
import {
  LIMITE_CONTEUDO_HTML,
  conteudoMensagemVazio,
  sanitizarHtmlMensagem,
} from "../../../components/MensagensGlobais/htmlMensagem";
import "../../../components/MensagensGlobais/mensagemGlobal.css";
import { UserContext } from "../../../contexts/UserContext";
import { mensagemGlobalService } from "../../../services/resources/mensagemGlobalService";
import { conectarMensagensGlobais } from "../../../services/resources/mensagemGlobalSocket";
import { DATA_FORMATO, podeGerenciarMensagensGlobais, urls } from "../../../utils";

export const FormularioMensagemGlobal = ({ onCreate, onView }) => {
  const history = useHistory();
  const { codigo } = useParams();
  const { user } = useContext(UserContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState(null);

  const autorizado = podeGerenciarMensagensGlobais(user);
  const somenteLeitura = Boolean(onView);

  useEffect(() => {
    if (!autorizado || !codigo || onCreate) return;
    async function carregar() {
      setLoading(true);
      try {
        const { data } = await mensagemGlobalService.buscarAdmin(codigo);
        setMensagem(data);
        form.setFieldsValue({
          titulo: data.titulo,
          conteudo: data.conteudo,
        });
      } catch (error) {
        openNotification(
          "error",
          <strong>Erro ao carregar mensagem</strong>,
          error?.response?.data?.userMessage || error?.response?.data?.detail
        );
      } finally {
        setLoading(false);
      }
    }
    carregar();
  }, [autorizado, codigo, form, onCreate]);

  useEffect(() => {
    if (!autorizado || !somenteLeitura || !codigo) return undefined;
    return conectarMensagensGlobais({
      onLeitura: (evento) => {
        if (String(evento?.codigo) !== String(codigo)) return;
        setMensagem((atual) =>
          atual
            ? {
                ...atual,
                quantidadeLeituras:
                  evento.quantidadeLeituras ?? atual.quantidadeLeituras,
              }
            : atual
        );
      },
    });
  }, [autorizado, somenteLeitura, codigo]);

  async function enviar(values) {
    setSalvando(true);
    try {
      await mensagemGlobalService.criar({
        titulo: values.titulo,
        conteudo: sanitizarHtmlMensagem(values.conteudo),
        destino: values.destino,
        codigosGrupos: values.destino === "GRUPO" ? values.codigosGrupos : [],
      });
      openNotification(
        "success",
        <strong>Mensagem enviada</strong>,
        values.destino === "GRUPO"
          ? "Somente os usuários dos grupos selecionados, já cadastrados, verão o aviso."
          : "Os usuários já cadastrados verão o aviso no próximo acesso."
      );
      history.push(urls.MENSAGENS_GLOBAIS_LIST);
    } catch (error) {
      openNotification(
        "error",
        <strong>Não foi possível enviar</strong>,
        error?.response?.data?.userMessage || error?.response?.data?.detail
      );
    } finally {
      setSalvando(false);
    }
  }

  if (!autorizado) {
    return (
      <div id="sectionMain">
        <Alert type="error" showIcon message="Acesso restrito ao grupo OISOL MASTER" />
      </div>
    );
  }

  return (
    <div id="sectionMain" className="pagina-mensagens-globais">
      <Breadcrumb id="breadcrumb">
        <Breadcrumb.Item>
          <Link to="">
            <HomeOutlined />
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>gerenciamento</Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={urls.MENSAGENS_GLOBAIS_LIST}>MENSAGENS GLOBAIS</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <strong>{somenteLeitura ? "detalhe" : "nova mensagem"}</strong>
        </Breadcrumb.Item>
      </Breadcrumb>
      <div className="container-section">
        <PageHeader
          titulo={somenteLeitura ? "DETALHE DA MENSAGEM" : "NOVA MENSAGEM GLOBAL"}
          subtitulo={
            somenteLeitura
              ? "Visualize o conteúdo enviado e o alcance da mensagem."
              : "Escreva o aviso e escolha se ele vai para todos os usuários ou para grupos específicos."
          }
        />
        <Spin spinning={loading}>
          <SecaoCard className="mensagem-card">
            {somenteLeitura && mensagem ? (
              <DetalheMensagem
                mensagem={mensagem}
                onVoltar={() => history.push(urls.MENSAGENS_GLOBAIS_LIST)}
              />
            ) : (
              <FormularioNovaMensagem
                form={form}
                salvando={salvando}
                onFinish={enviar}
                onVoltar={() => history.push(urls.MENSAGENS_GLOBAIS_LIST)}
              />
            )}
          </SecaoCard>
        </Spin>
      </div>
    </div>
  );
};

function DetalheMensagem({ mensagem, onVoltar }) {
  return (
    <>
      <div className="mensagem-meta">
        <Tag icon={<ClockCircleOutlined />}>
          {moment(mensagem.dataCadastro).format(`${DATA_FORMATO} HH:mm`)}
        </Tag>
        {mensagem.criadoPorNome && (
          <Tag icon={<UserOutlined />}>{mensagem.criadoPorNome}</Tag>
        )}
        {mensagem.destino === "GRUPO"
          ? (mensagem.grupos || []).map((grupo) => (
              <Tag key={grupo.codigo} icon={<TeamOutlined />} color="orange">
                {grupo.nome}
              </Tag>
            ))
          : (
              <Tag icon={<TeamOutlined />}>todos os usuários</Tag>
            )}
        <Tag icon={<EyeOutlined />} color="blue">
          {mensagem.quantidadeLeituras ?? 0} leitura
          {(mensagem.quantidadeLeituras ?? 0) === 1 ? "" : "s"}
        </Tag>
      </div>
      <Typography.Title level={3} className="mensagem-titulo-destaque">
        {mensagem.titulo}
      </Typography.Title>
      <ConteudoFormatado
        conteudo={mensagem.conteudo}
        className="conteudo-mensagem-scroll"
      />
      <Space style={{ marginTop: 24 }}>
        <Button onClick={onVoltar}>voltar</Button>
      </Space>
    </>
  );
}

function FormularioNovaMensagem({ form, salvando, onFinish, onVoltar }) {
  const destino = Form.useWatch("destino", form);
  const [grupos, setGrupos] = useState([]);

  useEffect(() => {
    async function carregarGrupos() {
      try {
        const { data } = await mensagemGlobalService.listarGrupos();
        setGrupos(Array.isArray(data) ? data : []);
      } catch (error) {
        openNotification(
          "error",
          <strong>Erro ao carregar grupos</strong>,
          error?.response?.data?.userMessage || error?.response?.data?.detail
        );
      }
    }
    carregarGrupos();
  }, []);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ destino: "TODOS" }}
    >
      <Form.Item
        name="titulo"
        label="título"
        rules={[
          { required: true, message: "Informe o título" },
          { max: 200, message: "Máximo de 200 caracteres" },
        ]}
      >
        <Input maxLength={200} size="large" placeholder="Ex.: Aviso importante" />
      </Form.Item>
      <Form.Item
        name="destino"
        label="enviar para"
        rules={[{ required: true, message: "Selecione o destino" }]}
      >
        <Radio.Group optionType="button" buttonStyle="solid">
          <Radio.Button value="TODOS">Todos os usuários</Radio.Button>
          <Radio.Button value="GRUPO">Grupos específicos</Radio.Button>
        </Radio.Group>
      </Form.Item>
      {destino === "GRUPO" && (
        <Form.Item
          name="codigosGrupos"
          label="grupos"
          rules={[{ required: true, message: "Selecione ao menos um grupo" }]}
        >
          <SelectBuscavel
            mode="multiple"
            allowClear
            placeholder="Selecione um ou mais grupos"
            options={grupos.map((grupo) => ({
              label: grupo.nome,
              value: grupo.codigo,
            }))}
          />
        </Form.Item>
      )}
      <Form.Item
        name="conteudo"
        label="conteúdo"
        rules={[
          {
            validator: (_, value) => {
              if (conteudoMensagemVazio(value)) {
                return Promise.reject(new Error("Informe o conteúdo"));
              }
              if ((value || "").length > LIMITE_CONTEUDO_HTML) {
                return Promise.reject(
                  new Error(`Máximo de ${LIMITE_CONTEUDO_HTML} caracteres`)
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <EditorConteudo />
      </Form.Item>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Somente usuários cadastrados até o momento do envio receberão esta mensagem."
      />
      <Row justify="end">
        <Space>
          <Button onClick={onVoltar}>voltar</Button>
          <Button type="primary" htmlType="submit" loading={salvando}>
            enviar
          </Button>
        </Space>
      </Row>
    </Form>
  );
}
